import * as Sentry from "@sentry/react";
import { normalizeSentryRoutePathname } from "./sentryRouteNormalization";

const TAG_CONTEXT_FIELDS = [
  "feature",
  "action",
  "contentBoxType",
  "route",
  "userVisible",
  "failureCategory",
  "errorCode",
  "networkOnline",
  "connectionEffectiveType",
];

const SAFE_EXTRA_FIELDS = new Set([
  "action",
  "affectedAnnouncementCount",
  "affectedItemCount",
  "assignmentCount",
  "automaticAttemptCount",
  "autoplay",
  "blackoutDateCount",
  "byteLength",
  "classCount",
  "classId",
  "classScopedEventCount",
  "classStyleSchemesLoadStatus",
  "committedItemCount",
  "conflictItemCount",
  "connectionEffectiveType",
  "connectionSaveData",
  "connectionType",
  "contentBoxCount",
  "contentBoxId",
  "contentBoxType",
  "contentBoxesLoadStatus",
  "autoRetried",
  "bootstrapSyncSource",
  "deletedCount",
  "durationMs",
  "eventCount",
  "expectedQueryCount",
  "extra",
  "feature",
  "failedItemCount",
  "failureCategory",
  "destinationDayKey",
  "destinationIndex",
  "draggableId",
  "hasDraft",
  "hasDataImage",
  "hasSchoolYearSettings",
  "hasStoredUrl",
  "hasUrl",
  "hasUser",
  "isRetry",
  "isSyncMode",
  "linkedBox",
  "linkedClassCount",
  "missingCount",
  "msSincePresentOpened",
  "nestedInMultiBox",
  "networkOnline",
  "operationId",
  "parentMultiBoxId",
  "partialWrite",
  "progressPercent",
  "queryIndex",
  "receivedQueryCount",
  "regenerateMultiBoxInnerIds",
  "retryingItemCount",
  "retryCount",
  "rotateGateVisible",
  "route",
  "scheduleCalendarLoadStatus",
  "scheduleRuntimeMode",
  "schemeCount",
  "scopeKey",
  "selectedBoxCount",
  "selectedClassCount",
  "severity",
  "shiftDays",
  "showClock",
  "showMarquee",
  "sourceClassId",
  "sourceDayKey",
  "sourceIndex",
  "startDate",
  "status",
  "timeToFailureMs",
  "timeToRecoveryMs",
  "syncSource",
  "targetBoxId",
  "teacherHubEventsLoadStatus",
  "templateCount",
  "uid",
  "endDate",
  "errorCode",
  "updatedKeys",
  "userVisible",
]);

const VALID_LEVELS = new Set(["fatal", "error", "warning", "log", "info", "debug"]);
const reportedDedupeKeys = new Set();

function normalizeLevel(severity) {
  if (!severity) return null;
  const normalized = String(severity).toLowerCase();
  if (normalized === "warn") return "warning";
  return VALID_LEVELS.has(normalized) ? normalized : null;
}

function normalizeRouteForTag(route) {
  if (!route) return "";
  return normalizeSentryRoutePathname(route);
}

function sanitizeExtraValue(value, depth = 0) {
  if (value == null) return value;
  if (depth > 2) return "[Filtered]";

  if (value instanceof Error) {
    return {
      name: value.name,
      code: value.code,
    };
  }

  if (Array.isArray(value)) {
    return {
      itemCount: value.length,
    };
  }

  if (typeof value === "object") {
    return Object.entries(value).reduce((acc, [key, nestedValue]) => {
      if (!SAFE_EXTRA_FIELDS.has(key)) {
        return acc;
      }
      acc[key] = sanitizeExtraValue(nestedValue, depth + 1);
      return acc;
    }, {});
  }

  if (typeof value === "string" && value.length > 180) {
    return "[Filtered]";
  }

  return value;
}

function buildError(error) {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === "string") {
    return new Error(error);
  }
  return new Error("Application error");
}

export function addAppBreadcrumb({ feature, action, message, data, level = "info" } = {}) {
  Sentry.addBreadcrumb({
    category: feature || "app",
    message: message || action || feature || "app_event",
    level: normalizeLevel(level) || "info",
    data: sanitizeExtraValue(data || {}),
  });
}

export function reportAppError(error, context = {}) {
  if (context.dedupeKey) {
    if (reportedDedupeKeys.has(context.dedupeKey)) {
      return;
    }
    reportedDedupeKeys.add(context.dedupeKey);
  }

  const capturedError = buildError(error);

  Sentry.withScope((scope) => {
    TAG_CONTEXT_FIELDS.forEach((field) => {
      const value = context[field];
      if (value !== undefined && value !== null && value !== "") {
        scope.setTag(field, field === "route" ? normalizeRouteForTag(value) : String(value));
      }
    });

    const level = normalizeLevel(context.severity);
    if (level) {
      scope.setLevel(level);
    }

    if (context.uid && typeof scope.setUser === "function") {
      scope.setUser({ id: String(context.uid) });
    }

    scope.setContext("app_error", sanitizeExtraValue(context));

    Sentry.captureException(capturedError);
  });
}

export function reportUserVisibleFailure(error, context = {}) {
  reportAppError(error, {
    ...context,
    feature: context.feature || "app",
    action: context.action || "user_visible_failure",
    severity: context.severity || "error",
    userVisible: true,
  });
}

export function reportAppEvent(message, context = {}) {
  Sentry.withScope((scope) => {
    TAG_CONTEXT_FIELDS.forEach((field) => {
      const value = context[field];
      if (value !== undefined && value !== null && value !== "") {
        scope.setTag(field, field === "route" ? normalizeRouteForTag(value) : String(value));
      }
    });

    scope.setLevel(normalizeLevel(context.severity) || "info");

    if (context.uid && typeof scope.setUser === "function") {
      scope.setUser({ id: String(context.uid) });
    }

    scope.setContext("app_event", sanitizeExtraValue(context));
    Sentry.captureMessage(message);
  });
}
